import { createTheme } from '@mui/material/styles';
import * as C from './colors.constants';

/**
 * Tema MUI v5 do protótipo.
 *
 * Decisões de escala:
 * - A resolução de referência é 1920x1200. Tudo que é "tamanho de caixa"
 *   (larguras de coluna, gaps, alturas de card) é fluido — % / flex / clamp —
 *   para que resoluções MAIORES apenas cresçam, sem reflow.
 * - A tipografia usa `clamp()` com base em 1920px: em telas maiores o texto
 *   acompanha proporcionalmente até um teto, evitando fonte minúscula em 4K.
 * - Breakpoint `xxl` (2560) foi adicionado para os ajustes de 4K/ultrawide.
 */
const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920, xxl: 2560,
    },
  },
  palette: {
    primary: {
      main: C.navy, dark: C.navyDark, light: C.navyLight, contrastText: C.white,
    },
    secondary: { main: C.teal, contrastText: C.white },
    success: { main: C.midGreen },
    warning: { main: C.tangerine },
    error: { main: C.tomato },
    background: { default: C.pageBackground, paper: C.white },
    text: { primary: C.black, secondary: C.warmGrey, disabled: C.disabledText },
    divider: C.borderGrey,
    action: { disabledBackground: C.disabledBg, disabled: C.disabledText },
  },
  shape: { borderRadius: C.radiusInput },
  typography: {
    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
    // clamp(min, escala fluida a partir de 1920, max)
    fontSize: 14,
    htmlFontSize: 16,
    h1: { fontSize: 'clamp(20px, 1.15vw, 28px)', fontWeight: 700, color: C.navy },
    h2: { fontSize: 'clamp(17px, 0.95vw, 24px)', fontWeight: 700, color: C.navy },
    h3: { fontSize: 'clamp(15px, 0.85vw, 21px)', fontWeight: 700, color: C.navy },
    subtitle1: { fontSize: 'clamp(13px, 0.75vw, 18px)', fontWeight: 600 },
    body1: { fontSize: 'clamp(13px, 0.73vw, 17px)' },
    body2: { fontSize: 'clamp(12px, 0.66vw, 16px)' },
    caption: { fontSize: 'clamp(11px, 0.60vw, 14px)' },
    button: { textTransform: 'none', fontWeight: 700 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: C.pageBackground, color: C.black },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          background: '#c3cbd6',
          borderRadius: 8,
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '*::-webkit-scrollbar-thumb:hover': { background: '#a6b0be', backgroundClip: 'content-box' },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { borderRadius: C.radiusCard, boxShadow: C.shadowCard, backgroundImage: 'none' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: C.radiusPill,
          fontWeight: 700,
          letterSpacing: 0.3,
          paddingInline: 20,
          minHeight: 34,
          // `normal` (e não `nowrap`) de propósito: rótulos longos como
          // "DEMONSTRATIVO POR TÍTULO" travavam a coluna do grid no seu
          // min-content e geravam scroll horizontal na aba Financeira.
          whiteSpace: 'normal',
          lineHeight: 1.25,
          minWidth: 0,
        },
        containedPrimary: {
          backgroundColor: C.navy,
          '&:hover': { backgroundColor: C.navyDark },
        },
        outlinedPrimary: {
          borderColor: C.navy,
          color: C.navy,
          backgroundColor: C.white,
          '&:hover': { backgroundColor: '#f0f4fa', borderColor: C.navy },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: C.white,
          borderRadius: C.radiusInput,
          '& fieldset': { borderColor: C.borderGrey },
          '&:hover fieldset': { borderColor: '#b6c0cd' },
          '&.Mui-focused fieldset': { borderColor: C.navy, borderWidth: 1.5 },
        },
        input: { paddingBlock: 9 },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { fontSize: 'clamp(12px, 0.66vw, 16px)' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 'clamp(12px, 0.68vw, 16px)',
          '&.Mui-selected': { backgroundColor: '#eaf0f8' },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: C.navy, fontSize: 12, padding: '6px 10px' },
        arrow: { color: C.navy },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${C.borderGrey}`, padding: '6px 8px' },
        // Cabeçalhos podem quebrar em duas linhas ("Dias com Processo",
        // "Nome do Devedor"): com `nowrap` eles transbordavam a própria coluna
        // e se sobrepunham ao cabeçalho vizinho quando a grid é estreitada.
        head: {
          color: C.navy, fontWeight: 700, whiteSpace: 'normal', lineHeight: 1.2, verticalAlign: 'bottom',
        },
      },
    },
    MuiCheckbox: { styleOverrides: { root: { color: '#9aa3af', padding: 4 } } },
    MuiSwitch: { styleOverrides: { root: { padding: 8 } } },
  },
});

export default theme;
